import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useUploadFile } from './useUploadFile';
import { useNostrPublish } from './useNostrPublish';

interface UploadPictureParams {
  file: File;
  caption?: string;
  title?: string;
  hashtags?: string[];
  /** NIP-36 content warning reason. Pass empty string for a generic CW. */
  contentWarning?: string;
}

/** Upload an image to Blossom and publish a kind 20 event. */
export function useUploadPicture() {
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, caption, title, hashtags, contentWarning }: UploadPictureParams) => {
      // Upload file to Blossom
      const fileTags = await uploadFile(file);

      // Extract URL and metadata from returned tags
      const urlTag = fileTags.find(([name]) => name === 'url');
      const url = urlTag?.[1];
      if (!url) throw new Error('Upload failed: no URL returned');

      const xTag = fileTags.find(([name]) => name === 'x');
      const hash = xTag?.[1];
      const mimeTag = fileTags.find(([name]) => name === 'm');
      const mimeType = mimeTag?.[1] ?? file.type;
      const sizeTag = fileTags.find(([name]) => name === 'size');

      // Build imeta tag entries
      const imetaEntries: string[] = [
        'imeta',
        `url ${url}`,
        `m ${mimeType}`,
      ];
      if (hash) imetaEntries.push(`x ${hash}`);
      if (sizeTag?.[1]) imetaEntries.push(`size ${sizeTag[1]}`);
      imetaEntries.push(`alt ${caption || title || 'Photo'}`);

      // Build event tags
      const tags: string[][] = [imetaEntries];
      if (title) tags.push(['title', title]);
      if (mimeType) tags.push(['m', mimeType]);
      if (hash) tags.push(['x', hash]);
      if (hashtags) {
        for (const tag of hashtags) {
          tags.push(['t', tag]);
        }
      }
      // NIP-36 content warning
      if (contentWarning !== undefined) {
        tags.push(['content-warning', contentWarning]);
        tags.push(['L', 'content-warning']);
        tags.push(['l', contentWarning || 'content-warning', 'content-warning']);
      }

      // Publish kind 20 event
      const event = await createEvent({
        kind: 20,
        content: caption ?? '',
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      return event;
    },
    onSuccess: () => {
      // Invalidate feed and user album queries
      queryClient.invalidateQueries({ queryKey: ['pictures'] });
    },
  });
}
